import * as RDialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const { t } = useTranslation();
  const width = {
    sm: 'max-w-md',
    md: 'max-w-xl',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
  }[size];
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-[90] bg-ink-navy/40 backdrop-blur-[1px]" />
        <RDialog.Content
          // Radix warns when no <Description> is rendered. When this dialog has no
          // description, opt out explicitly (silences the warning); when it does, leave the
          // prop unset so Radix auto-wires aria-describedby to the Description id.
          {...(description ? {} : { 'aria-describedby': undefined })}
          className={cn(
            'fixed start-1/2 top-1/2 z-[91] flex max-h-[88vh] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border-[0.5px] border-hairline bg-canvas-paper rtl:translate-x-1/2',
            width,
          )}
        >
          <header className="flex items-start justify-between gap-md border-b-[0.5px] border-hairline p-lg">
            <div>
              <RDialog.Title className="text-title text-ink-deep">{title}</RDialog.Title>
              {description && (
                <RDialog.Description className="mt-2xs text-small text-ink-mid">
                  {description}
                </RDialog.Description>
              )}
            </div>
            <RDialog.Close
              aria-label={t('ui.close')}
              className="rounded-sm px-xs text-ink-mute hover:text-ink-deep focus-visible:shadow-focus"
            >
              ✕
            </RDialog.Close>
          </header>
          <div className="overflow-auto p-lg">{children}</div>
          {footer && (
            <footer className="flex justify-end gap-sm border-t-[0.5px] border-hairline p-lg">
              {footer}
            </footer>
          )}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
